import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  PARTNER_DEAL_REJECTION_REASON_LABELS,
  PARTNER_DEAL_SYSTEM_REJECTION_REASONS,
  PartnerDealRejectionReason,
} from '@interfaces/partners/responses/partner-deal-timeline.response';
import { PartnerDealResponse } from '@interfaces/partners/responses/partner-deal.response';
import { Option } from '@interfaces/common/option';
import { TranslateService } from '@ngx-translate/core';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { PartnersHttpService } from '@services/http/partners/partners-http.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { SharedModule } from '@src/app/shared/shared.module';
import { VaultMaskCnpjPipe } from '@shared/pipes/vault-mask-cnpj.pipe';
import { first } from 'rxjs';

/** O que esta modal está decidindo. */
export type DealDecisionMode = 'rejeitar' | 'reabrir';

/**
 * Rejeição e reabertura de um deal pela Legatus (card 2952, bloco A).
 *
 * **Uma modal para os dois** porque a pergunta é a mesma — *por quê?* — e a resposta vai para o
 * mesmo lugar: a linha do tempo, que o parceiro lê. Separar em dois componentes duplicaria o
 * formulário, a validação e o texto de ajuda para trocar um rótulo.
 *
 * A **pré-aprovação não passa por aqui**: ela não pede motivo nem justificativa, então uma modal
 * com um campo vazio seria um passo a mais sem nada a preencher. Ela é uma confirmação simples na
 * própria tela do deal.
 *
 * ⚠️ **O texto é obrigatório nos dois modos**, e na rejeição isso vale inclusive quando o motivo
 * não é "Outro": o rótulo estruturado serve para contar e filtrar, mas o que faz o parceiro
 * entender o que houve — e o que ele pode corrigir — é o texto. Rejeição só com rótulo vira
 * pergunta de volta.
 */
@Component({
  selector: 'app-modal-deal-decision',
  templateUrl: './modal-deal-decision.component.html',
  styleUrls: ['./modal-deal-decision.component.scss'],
  imports: [CommonModule, ReactiveFormsModule, SharedModule, VaultMaskCnpjPipe],
})
export class ModalDealDecisionComponent extends VaultBaseComponent {
  @ViewChild('dialog') dialogRef: DialogComponent | null = null;

  /** Avisa a tela do deal para recarregar. */
  @Output() decidiu = new EventEmitter<void>();

  /** Deal em contexto — exibido no cabeçalho para a pessoa conferir antes de decidir. */
  deal: PartnerDealResponse | null = null;

  modo: DealDecisionMode = 'rejeitar';

  /** Ver o comentário homônimo na modal de assinantes: não use `isLoading`. */
  gravando = false;

  /** Motivos na grafia do backend, com os rótulos que a linha do tempo já usa. */
  readonly motivos: Option[] = (
    Object.keys(PARTNER_DEAL_REJECTION_REASON_LABELS) as PartnerDealRejectionReason[]
  )
    // "Negado pelo comitê de crédito" só sai da votação do comitê (card 3789).
    .filter(valor => !PARTNER_DEAL_SYSTEM_REJECTION_REASONS.includes(valor))
    .map(valor => ({ label: PARTNER_DEAL_REJECTION_REASON_LABELS[valor], value: valor }));

  form: FormGroup = this.formBuilder.group({
    reason: [null as PartnerDealRejectionReason | null],
    notes: ['', [Validators.required, Validators.minLength(10)]],
  });

  constructor(
    translateService: TranslateService,
    private formBuilder: FormBuilder,
    private partnersService: PartnersHttpService,
    private toast: ToastHelperService,
    private cdr: ChangeDetectorRef
  ) {
    super(translateService);
  }

  get rejeitando(): boolean {
    return this.modo === 'rejeitar';
  }

  get titulo(): string {
    return this.rejeitando ? 'Rejeitar deal' : 'Reabrir deal';
  }

  get rotuloDoBotao(): string {
    return this.rejeitando ? 'Rejeitar deal' : 'Reabrir deal';
  }

  get rotuloDoTexto(): string {
    return this.rejeitando ? 'O que o parceiro precisa saber' : 'Por que está sendo reaberto';
  }

  get exemploDoTexto(): string {
    return this.rejeitando
      ? 'Ex.: faturamento abaixo do mínimo que atendemos hoje. Se houver operação de câmbio recorrente, podemos reavaliar.'
      : 'Ex.: o cliente enviou o balanço que faltava; a restrição apontada não se confirmou.';
  }

  /**
   * Reabertura de deal negado pelo comitê de crédito (card 3789): o servidor o devolve para Análise,
   * para uma nova rodada, e não para a etapa em que parou.
   */
  reopensToAnalysis = false;

  /** Abre a modal no modo pedido, com o formulário limpo. */
  abrir(deal: PartnerDealResponse, modo: DealDecisionMode, reopensToAnalysis = false): void {
    this.deal = deal;
    this.modo = modo;
    this.reopensToAnalysis = modo === 'reabrir' && reopensToAnalysis;
    this.gravando = false;

    this.form.reset({ reason: null, notes: '' });

    // O motivo é exigido só na rejeição. Reabrir não tem catálogo de motivos: o que importa ali
    // é a justificativa, que fica na linha do tempo ao lado da rejeição que ela desfez.
    const reason = this.form.get('reason');
    if (this.rejeitando) reason?.setValidators([Validators.required]);
    else reason?.clearValidators();
    reason?.updateValueAndValidity();

    this.cdr.markForCheck();
    this.dialogRef?.open('', '', '', 'Cancelar', '', 'deal-decision', false, true);
  }

  /** Fecha sem decidir. */
  fechar(): void {
    this.dialogRef?.close();
  }

  isFormValid(): boolean {
    return this.form.valid && !this.gravando;
  }

  /** Envia a decisão. */
  submitAction(): void {
    const deal = this.deal;
    if (!deal || !this.isFormValid()) return;

    const notes = ((this.form.get('notes')?.value as string) ?? '').trim();

    this.gravando = true;
    this.cdr.markForCheck();

    const chamada = this.rejeitando
      ? this.partnersService.rejectDeal(deal.id, {
          reason: this.form.get('reason')?.value as PartnerDealRejectionReason,
          notes,
        })
      : this.partnersService.revertDealRejection(deal.id, { notes });

    chamada.pipe(first()).subscribe({
      next: () => {
        this.gravando = false;
        this.toast.showToastSuccess(
          this.rejeitando ? 'Deal rejeitado. O parceiro foi avisado.' : 'Deal reaberto.'
        );
        this.decidiu.emit();
        this.fechar();
        this.cdr.markForCheck();
      },
      error: erro => {
        this.gravando = false;
        // A mensagem do backend é a que diz o que fazer ("Este deal já está rejeitado", por
        // exemplo). Trocá-la por um texto genérico esconde justamente a informação útil.
        this.toast.showToastError(
          erro?.errors?.[0]?.message ??
            (this.rejeitando
              ? 'Não foi possível rejeitar o deal.'
              : 'Não foi possível reabrir o deal.')
        );
        this.cdr.markForCheck();
      },
    });
  }
}
